import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';

import {
  LngLatBounds,
  Map,
  Marker,
  MarkerOptions,
  NavigationControl,
  Popup,
} from 'maplibre-gl';
import { createMap } from 'maplibre-gl-js-amplify';
import * as turf from '@turf/turf';
import {
  ShipmentGeofencePolygonViewModel,
  ShipmentLocationViewModel,
} from '../shipment-location-list-table/shipment-location-list-table.component';
import { ShipmentGeofencePolygonDto } from '@entities/shipment-location';
import { Feature, GeoJsonProperties, Polygon } from 'geojson';

import {
  createDeviceMarkerElement,
  DEVICE_MARKER_HEIGHT,
} from '@utils/device-marker.util';

const blue = '#558ED5';

export type ShipmentLocationDeviceViewModel = {
  deviceName: string;
  serialNo: string;
  latitude: number;
  longitude: number;
};

@Component({
  selector: 'app-shipment-location-map',
  standalone: true,
  imports: [],
  templateUrl: './shipment-location-map.component.html',
  styleUrl: './shipment-location-map.component.scss',
})
export class ShipmentLocationMapComponent implements OnInit, OnChanges {
  /** mapのHTML要素のid */
  @Input() mapId: string = 'map';

  /** 位置情報 */
  @Input() location: ShipmentLocationViewModel | undefined;

  /** 追加の位置情報リスト */
  @Input() additionalLocationList: ShipmentLocationViewModel[] = [];

  /** デバイス位置情報リスト */
  @Input() deviceLocationList: ShipmentLocationDeviceViewModel[] = [];

  /** マップを編集可能か */
  @Input() editable = false;

  /** ポリゴン型ジオフェンス編集情報を通知 */
  @Output() polygonEdited = new EventEmitter<ShipmentGeofencePolygonDto[]>();

  /** マップをクリックしたことを通知 */
  @Output() mapClicked = new EventEmitter<ShipmentGeofencePolygonViewModel>();

  private map!: Map;
  /** 地図上のマーカー */
  private marker: Marker | undefined;
  private additionalMarkers: Marker[] = [];
  private deviceMarkers: Marker[] = [];
  private editableMarkers: Marker[] = [];
  private mapReady = false;

  /** ジオフェンス */
  private geofencePolygon: Feature<Polygon, GeoJsonProperties> | undefined;

  /** ポップアップリスト */
  private popupList: Popup[] = [];

  async ngOnInit() {
    this.map = await createMap({
      container: this.mapId,
      center: [139.767, 35.681],
      zoom: 11,
      minZoom: 1,
    });

    this.map.addControl(
      new NavigationControl({
        visualizePitch: true,
      }),
    );

    await this.waitUntilMapReady();
    this.mapReady = true;

    this.map.on('click', (e) => {
      // クリック位置経緯度取得
      const lat = e.lngLat.lat;
      const lng = e.lngLat.lng;

      const lngLat: ShipmentGeofencePolygonViewModel = {
        latitude: lat,
        longitude: lng,
      };

      // あるマーカーのポップアップを表示している状態で、別のマーカーをクリックしたときにポップアップが残り続けないようにする
      this.clearPopupList();

      // 経緯度表示
      this.mapClicked.emit(lngLat);
    });

    // `@Input` が map 初期化前に更新された場合でも、初期表示で反映する。
    if (this.location) {
      await this.setMainLayers();
    }
    if (this.additionalLocationList?.length) {
      await this.setAdditionalLayers();
    }
    if (this.deviceLocationList?.length) {
      await this.setDeviceLayers();
    }
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (!this.mapReady) {
      return;
    }

    if (changes['location'] !== undefined) {
      // 緯度経度が変わったときだけpanする
      const preLocation = changes['location'].previousValue;
      const currentLocation = changes['location'].currentValue;
      // 選択されている行がなくなった場合は、マップを初期化する
      if (currentLocation === undefined) {
        // マップが表示するまで待機
        if (!this.mapReady) {
          return;
        }
        // マーカーの初期化
        this.marker?.remove();
        // ジオフェンスの初期化
        this.geofencePolygon = undefined;
        this.initGeofence('main', this.editable);
        // ポップアップのクリア
        this.clearPopupList();
        return;
      }

      const pan =
        preLocation?.latitude !== currentLocation?.latitude ||
        preLocation?.longitude !== currentLocation?.longitude;

      // マップにメインの位置情報を表示する
      this.setMainLayers(pan).then();
    }

    if (changes['additionalLocationList'] !== undefined) {
      // マップに追加の位置情報を表示する
      this.setAdditionalLayers().then();
    }

    if (changes['deviceLocationList'] !== undefined) {
      this.setDeviceLayers().then();
    }
  }

  private async setMainLayers(pan = true) {
    // マップが表示するまで待機
    if (!this.mapReady) {
      return;
    }

    // マーカー表示
    this.setMarker(this.location);

    // PANする
    if (pan) {
      this.panToMarkers(this.marker, this.geofencePolygon);
    }
  }

  private async setAdditionalLayers() {
    // マップが表示するまで待機
    if (!this.mapReady) {
      return;
    }

    // 追加のマーカー表示
    this.setAdditionalMarkers(this.additionalLocationList);
  }

  private async setDeviceLayers() {
    // マップが表示するまで待機
    if (!this.mapReady) {
      return;
    }

    this.setDeviceMarkers(this.deviceLocationList);
  }

  private setMarker(location: ShipmentLocationViewModel | undefined): void {
    // 初期化
    this.marker?.remove();

    if (!location) return;

    // ジオフェンスの初期化
    this.geofencePolygon = undefined;
    this.initGeofence('main', this.editable);

    // ジオフェンス表示
    this.geofencePolygon = this.createGeofence(location, 'main', this.editable);

    // マーカー追加
    const longitude = location.longitude;
    const latitude = location.latitude;
    const creatingMarkerOptions: MarkerOptions = {
      color: '#000000',
      scale: 1.5,
    };
    this.marker = new Marker(creatingMarkerOptions)
      .setLngLat({ lat: latitude, lng: longitude })
      .addTo(this.map);

    // ポップアップを設定
    this.setPopup(
      this.marker,
      this.createLocationDescription(location.locationName || '', location),
    );
  }

  private setAdditionalMarkers(
    additionalLocationList: ShipmentLocationViewModel[],
  ): void {
    // マーカーの初期化
    this.additionalMarkers.forEach((marker) => marker.remove());
    this.additionalMarkers = [];

    // ジオフェンスの初期化
    this.initGeofence('additional');

    if (!additionalLocationList) return;

    for (const additionalLocation of additionalLocationList) {
      const longitude = additionalLocation.longitude;
      const latitude = additionalLocation.latitude;

      // 位置情報がない場合は表示しないようにガード
      if (longitude === undefined || latitude === undefined) {
        continue;
      }

      // ジオフェンス表示
      this.createGeofence(additionalLocation, 'additional');

      const markerOptions: MarkerOptions = {
        color: '#9CA3AF',
      };
      const marker = new Marker(markerOptions)
        .setLngLat([longitude, latitude])
        .addTo(this.map);
      this.additionalMarkers.push(marker);

      // ポップアップを設定
      this.setPopup(
        marker,
        this.createLocationDescription(
          additionalLocation.locationName || '',
          additionalLocation,
        ),
      );
    }
  }

  private setDeviceMarkers(
    deviceLocationList: ShipmentLocationDeviceViewModel[],
  ): void {
    this.deviceMarkers.forEach((marker) => marker.remove());
    this.deviceMarkers = [];

    if (!deviceLocationList) return;

    for (const deviceLocation of deviceLocationList) {
      const markerOptions: MarkerOptions = {
        element: createDeviceMarkerElement(),
        anchor: 'bottom',
      };
      const marker = new Marker(markerOptions)
        .setLngLat({
          lng: deviceLocation.longitude,
          lat: deviceLocation.latitude,
        })
        .addTo(this.map);
      this.deviceMarkers.push(marker);

      this.setPopup(
        marker,
        this.createDeviceDescription(deviceLocation),
        DEVICE_MARKER_HEIGHT,
      );
    }
  }

  /**
   * 地図上に表示されているマーカーが見えるように地図をPANする。
   * @param markers マーカー
   */
  private panToMarkers(
    marker?: Marker,
    geofencePolygon?: Feature<Polygon, GeoJsonProperties>,
  ) {
    if (!marker) {
      this.map.setCenter([139.767, 35.681]);
      this.map.setZoom(11);
      return;
    }

    // マーカーの座標を包括する範囲を計算
    const bounds = new LngLatBounds();
    bounds.extend(marker.getLngLat());

    // ジオフェンス分の範囲も計算に追加する
    if (geofencePolygon) {
      const geofenceBounds = turf.bbox(geofencePolygon);
      bounds.extend([geofenceBounds[0], geofenceBounds[1]]);
      bounds.extend([geofenceBounds[2], geofenceBounds[3]]);
    }

    // マップをPANしてマーカーが見えるようにする
    this.map.fitBounds(bounds, { padding: 50, maxZoom: 16, animate: false });
  }

  private async waitUntilMapReady(): Promise<void> {
    if (this.map.loaded()) {
      return;
    }

    await new Promise<void>((resolve) => {
      this.map.once('load', () => {
        resolve();
      });
    });
  }

  /** ポップアップを設定する */
  private setPopup(
    marker: Marker | undefined,
    description: string,
    verticalOffset = 20,
  ) {
    // マーカーホバー時に表示されるポップアップの挙動設定
    const popup = new Popup({
      closeButton: false,
      closeOnClick: false,
    });

    // マーカーホバー時の挙動設定
    marker?.getElement().addEventListener('mouseenter', () => {
      this.popupList.push(popup);

      marker.getElement().style.cursor = 'pointer';
      const longitude = marker.getLngLat().lng;
      const latitude = marker.getLngLat().lat;
      if (longitude !== undefined && latitude !== undefined) {
        popup
          .setLngLat([longitude, latitude])
          .setHTML(description)
          .setOffset([0, -verticalOffset])
          .addTo(this.map);

        this.adjustPopupPosition(popup, verticalOffset);
      }
    });
    marker?.getElement().addEventListener('mouseleave', () => {
      marker.getElement().style.cursor = '';
      this.clearPopupList();
    });
  }

  /**
   * マーカーのツールチップポップアップのテキストを生成する
   * @param deviceName
   * @param sensingType
   * @param value
   * @param eventAt
   * @returns
   */
  private createLocationDescription(
    locationName: string,
    location: ShipmentLocationViewModel,
  ): string {
    let geofenceText: string = '';
    const address = location.address;
    const geofence = location.geofence;
    if (geofence?.geofenceType === 'circle') {
      geofenceText = `円  半径${geofence.circleRadius}${geofence.circleRadiusUnit}`;
    } else if (geofence?.geofenceType === 'polygon') {
      // geofence.polygonListは始点と終点で重複してカウントするため1を引く
      geofenceText = `フリー描画  ${geofence.polygonList.length - 1} 頂点`;
    }

    const description = `
        <div class="tw-font-bold">${locationName}</div>
        <div>ジオフェンス：${geofenceText}</div>
        <div>住所：${address}</div>
    `;

    return description;
  }

  private createDeviceDescription(
    deviceLocation: ShipmentLocationDeviceViewModel,
  ): string {
    return `
        <div>デバイス名：${deviceLocation.deviceName}</div>
        <div>シリアル番号：${deviceLocation.serialNo}</div>
    `;
  }

  /** 位置情報からジオフェンスを表示する */
  private createGeofence(
    location: ShipmentLocationViewModel,
    prefix: string,
    polygonEditable = false,
  ): Feature<Polygon, GeoJsonProperties> | undefined {
    const fillColor = blue;
    const fillOpacity = 0.33;
    const paint = {
      'fill-color': fillColor,
      'fill-opacity': fillOpacity,
    };

    const latitude = location.latitude;
    const longitude = location.longitude;

    const geofence = location.geofence;
    if (!geofence) {
      return;
    }

    const geofenceType = geofence.geofenceType;
    const locationId = location.locationId ?? 'new';

    if (geofenceType === 'circle') {
      // Amazon Location Serviceにはメートル単位で指定するため、メートル単位で表示する
      const radius = geofence.circleRadiusInMeters;
      const options: { steps: number; units: turf.Units } = {
        steps: 64,
        units: 'meters',
      };
      const radiusCenter = [longitude, latitude];
      const circle = turf.circle(radiusCenter, radius, options);

      const sourceId = `${prefix}-circle-${locationId}-${latitude}-${longitude}`;
      this.map.addSource(sourceId, {
        type: 'geojson',
        data: circle,
      });

      const layerId = `${prefix}-circle-layer-${locationId}-${latitude}-${longitude}`;
      this.map.addLayer({
        id: layerId,
        type: 'fill',
        source: sourceId,
        layout: {},
        paint: paint,
      });

      return circle;
    } else if (geofenceType === 'polygon') {
      // 位置情報が4つ以上(最初の要素と最後の要素が一致することと3角形)でないと使用できないため早期リターン
      if (geofence.polygonList.length < 4) {
        return;
      }

      const sourceId = `${prefix}-polygon-${locationId}-${latitude}-${longitude}`;
      const coordinates = geofence.polygonList.map((polygon) => {
        return [polygon.longitude, polygon.latitude];
      });
      const polygon = turf.polygon([coordinates]);
      this.map.addSource(sourceId, {
        type: 'geojson',
        data: polygon,
      });

      const layerId = `${prefix}-polygon-layer-${locationId}-${latitude}-${longitude}`;
      this.map.addLayer({
        id: layerId,
        type: 'fill',
        source: sourceId,
        layout: {},
        paint: paint,
      });

      if (polygonEditable) {
        this.createEditableMarkers(geofence.polygonList, sourceId);
      }

      return polygon;
    }

    return;
  }

  /** ポリゴン編集可能なマーカーを表示する */
  private async createEditableMarkers(
    polygonList: ShipmentGeofencePolygonViewModel[] = [],
    sourceId: string,
  ) {
    if (polygonList.length < 4) {
      return;
    }

    // 最初と最後は同じ緯度経度なので除外してマーカーを表示する
    const displayPolygonList = polygonList.slice(0, polygonList.length - 1);

    for (const polygon of displayPolygonList) {
      // 〇の形のマーカーを作成するためのHTML要素
      const editableMarkerElement = document.createElement('div');
      editableMarkerElement.style.width = '25px';
      editableMarkerElement.style.height = '25px';
      editableMarkerElement.style.borderRadius = '50%';
      editableMarkerElement.style.backgroundColor = 'white';
      editableMarkerElement.style.border = `8px solid ${blue}`;

      const marker = new Marker({
        draggable: true,
        element: editableMarkerElement,
      })
        .setLngLat([polygon.longitude, polygon.latitude])
        .addTo(this.map);

      // マーカーが移動された時、ポリゴン表示を更新する
      marker.on('drag', () => {
        const newPolygonList = this.editableMarkers.map((marker) => {
          const lngLat = marker.getLngLat();
          const newPolygon: ShipmentGeofencePolygonDto = {
            latitude: lngLat.lat,
            longitude: lngLat.lng,
          };
          return newPolygon;
        });

        // 最後に最初と同じ頂点を追加する
        newPolygonList.push(newPolygonList[0]);

        // ポリゴン表示を更新
        const coordinates = newPolygonList.map((polygon) => {
          return [polygon.longitude, polygon.latitude];
        });
        const geoJson = turf.polygon([coordinates]);
        this.map.style.setGeoJSONSourceData(sourceId, geoJson);

        // 親コンポーネントに通知
        this.polygonEdited.emit(newPolygonList);
      });

      this.editableMarkers.push(marker);
    }
  }

  /** 既存のジオフェンスを初期化する */
  private initGeofence(prefix: string, polygonEditable = false): void {
    const layers = this.map.getStyle().layers || [];
    layers.forEach((layer) => {
      if (
        layer.id.startsWith(`${prefix}-circle-layer-`) ||
        layer.id.startsWith(`${prefix}-polygon-layer-`)
      ) {
        this.map.removeLayer(layer.id);
      }
    });

    const sources = this.map.getStyle().sources || {};
    Object.keys(sources).forEach((sourceId) => {
      if (
        sourceId.startsWith(`${prefix}-circle-`) ||
        sourceId.startsWith(`${prefix}-polygon-`)
      ) {
        this.map.removeSource(sourceId);
      }
    });

    // ポリゴン編集用マーカーの初期化
    if (polygonEditable) {
      this.editableMarkers.forEach((marker) => marker.remove());
      this.editableMarkers = [];
    }
  }

  /** ポップアップのクリア */
  private clearPopupList(): void {
    this.popupList.forEach((popup) => {
      popup.remove();
    });
    this.popupList = [];
  }

  /**
   * ツールチップが見切れている場合に位置を調整する
   * @param popup
   */
  private adjustPopupPosition(popup: Popup, verticalOffset = 20) {
    const popupElement = popup.getElement();
    const mapContainer = this.map.getContainer();

    // ポップアップの位置とサイズを取得
    const popupRect = popupElement.getBoundingClientRect();
    const mapRect = mapContainer.getBoundingClientRect();

    const isOutOfBounds = {
      top: popupRect.top < mapRect.top,
      bottom: popupRect.bottom > mapRect.bottom,
      left: popupRect.left < mapRect.left,
      right: popupRect.right > mapRect.right,
    };

    // 上側に見切れている場合、ポップアップを下に移動
    if (isOutOfBounds.top) {
      popup.setOffset([0, 20]);
      // 下側に見切れている場合、ポップアップを上に移動
    } else if (isOutOfBounds.bottom) {
      popup.setOffset([0, -verticalOffset]);
    }

    // 左側に見切れている場合、右に移動
    if (isOutOfBounds.left) {
      popup.setOffset([-20, 0]);
      // 右側に見切れている場合、左に移動
    } else if (isOutOfBounds.right) {
      popup.setOffset([20, 0]);
    }
  }
}
